import React, { useMemo } from "react"
import { ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import moment from "moment"

import FilledButtonM from "@common/components/button/FilledButtonM"
import { IconImageM } from "@common/components/icons/IconsM"
import ErrorTextM from "@common/components/text/ErrorTextM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"

import {
    DATETIME_FORMAT,
    DEADLINE_OPTIONS,
    FRENCH_DAY_BY_INT,
    formatHoursAsRanges,
    resolveExtraHourPricePerHour,
    slotStringToApi,
    stickyFooterStyle,
    validateForm,
} from "./utils"

const RecapRow = ({ label, value }) => (
    <View
        style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 4,
        }}
    >
        <TextM fontWeight="SemiBold">{label}</TextM>
        <TextM wrap style={{ flexShrink: 1, textAlign: "right" }}>
            {value}
        </TextM>
    </View>
)

/**
 * Step 3 — recap + submit. The submit button is rendered by the
 * FormStepperModal chrome (`submitButtonText`) but we also display a
 * read-only summary here. Clicking submit fires
 * `subscriptionService.createExtraHourRequest` via FormStepperModal.
 *
 * Surfaces local validation errors (mirrored from the backend rules)
 * in a banner above the submit button. Server-side 422s are NOT
 * rendered here — `FormStep` already paints `serverError` at the
 * bottom of the last step (see FormStep.js `isLastStep && <ErrorTextM
 * errorData={serverError} />`), so duplicating it would double the
 * message.
 */
export const RecapStep = ({
    formValues,
    schoolSubjects,
    goToNextStep,
    extraHourPricePerHour,
}) => {
    const hours = formValues?.hours ?? 1
    const format = formValues?.format ?? "shared"
    const isIndividual = format === "individual"
    const deadlineType = formValues?.deadline_type ?? "7_days"
    const customDeadline = formValues?.custom_deadline ?? null
    const subjectId = formValues?.subject_id ?? null
    const selectedSlots = formValues?.selected_slots ?? []
    const requestedDates = formValues?.requested_dates ?? []

    const indicativeTotal =
        resolveExtraHourPricePerHour(extraHourPricePerHour, format) * hours

    const selectedSubject = (schoolSubjects ?? []).find(
        (s) => s.id === subjectId
    )

    const deadlineLabel = (() => {
        if (deadlineType === "custom" && customDeadline) {
            return `Avant le ${moment(customDeadline).format("DD/MM/YYYY")}`
        }
        return DEADLINE_OPTIONS.find((d) => d.value === deadlineType)?.label
    })()

    const slotsByDay = useMemo(() => {
        const grouped = {}
        selectedSlots
            .map(slotStringToApi)
            .filter(Boolean)
            .forEach(({ day_of_week, hour }) => {
                const key = FRENCH_DAY_BY_INT[day_of_week]
                grouped[key] = grouped[key] ?? []
                grouped[key].push(hour)
            })
        Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a - b))
        return grouped
    }, [selectedSlots])

    const localErrors = useMemo(() => validateForm(formValues), [formValues])

    return (
        <View style={{ flex: 1, width: "100%" }}>
            <ScrollView
                style={{ width: "100%", flex: 1 }}
                contentContainerStyle={{
                    paddingHorizontal: 20,
                    paddingBottom: 12,
                }}
                keyboardShouldPersistTaps="handled"
            >
                <View
                    style={{
                        backgroundColor: theme.colors.greyBg,
                        padding: 14,
                        borderRadius: 10,
                        marginTop: 12,
                        marginBottom: 12,
                    }}
                >
                    <RecapRow label="Heures" value={`${hours}h`} />
                    <RecapRow
                        label="Format"
                        value={
                            format === "individual"
                                ? "Cours individuel"
                                : "Cours partagé"
                        }
                    />
                    <RecapRow
                        label="Total indicatif"
                        value={`${indicativeTotal} €`}
                    />
                    {!isIndividual && (
                        <RecapRow label="À traiter" value={deadlineLabel} />
                    )}
                    <RecapRow
                        label="Matière"
                        value={
                            selectedSubject ? (
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    {selectedSubject.icon && (
                                        <IconImageM
                                            uri={selectedSubject.icon}
                                            width={16}
                                        />
                                    )}
                                    <TextM
                                        style={{ marginLeft: 6 }}
                                        fontWeight="Bold"
                                    >
                                        {selectedSubject.name}
                                    </TextM>
                                </View>
                            ) : (
                                ""
                            )
                        }
                    />
                </View>

                <View style={{ marginBottom: 12 }}>
                    <TextM
                        fontWeight="SemiBold"
                        fontSize={13}
                        style={{ marginBottom: 8 }}
                    >
                        {isIndividual ? "Dates demandées" : "Créneaux préférés"}
                    </TextM>
                    {isIndividual ? (
                        requestedDates.length === 0 ? (
                            <TextM color={theme.colors.grey} fontSize={12}>
                                Aucune date sélectionnée
                            </TextM>
                        ) : (
                            <View style={{ flexDirection: "column" }}>
                                {requestedDates.map((value, i) => {
                                    const m = moment(
                                        value,
                                        DATETIME_FORMAT,
                                        true
                                    )
                                    if (!m.isValid()) return null
                                    return (
                                        <View
                                            key={`${value}-${i}`}
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                backgroundColor:
                                                    theme.colors.successLight,
                                                borderWidth: 1,
                                                borderColor:
                                                    theme.colors.success,
                                                borderRadius: 999,
                                                paddingVertical: 5,
                                                paddingHorizontal: 10,
                                                marginBottom: 6,
                                                alignSelf: "flex-start",
                                            }}
                                        >
                                            <FontAwesome5
                                                name="check"
                                                size={11}
                                                color={theme.colors.success}
                                                style={{ marginRight: 5 }}
                                            />
                                            <TextM
                                                fontWeight="Bold"
                                                fontSize={12}
                                                color={theme.colors.success}
                                            >
                                                {m.format("ddd DD/MM")} ·{" "}
                                                {m.format("HH[h]mm")}
                                            </TextM>
                                        </View>
                                    )
                                })}
                            </View>
                        )
                    ) : Object.keys(slotsByDay).length === 0 ? (
                        <TextM color={theme.colors.grey} fontSize={12}>
                            Aucun créneau sélectionné
                        </TextM>
                    ) : (
                        <View
                            style={{ flexDirection: "row", flexWrap: "wrap" }}
                        >
                            {Object.entries(slotsByDay).map(([day, hs]) => (
                                <View
                                    key={day}
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        backgroundColor:
                                            theme.colors.successLight,
                                        borderWidth: 1,
                                        borderColor: theme.colors.success,
                                        borderRadius: 999,
                                        paddingVertical: 5,
                                        paddingHorizontal: 10,
                                        marginRight: 6,
                                        marginBottom: 6,
                                    }}
                                >
                                    <FontAwesome5
                                        name="check"
                                        size={11}
                                        color={theme.colors.success}
                                        style={{ marginRight: 5 }}
                                    />
                                    <TextM
                                        fontWeight="Bold"
                                        fontSize={12}
                                        color={theme.colors.success}
                                    >
                                        {day} {formatHoursAsRanges(hs)}
                                    </TextM>
                                </View>
                            ))}
                        </View>
                    )}
                </View>

                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        backgroundColor: theme.colors.material.primary["50"],
                        borderRadius: 10,
                        padding: 12,
                        marginBottom: 12,
                    }}
                >
                    <FontAwesome5
                        name="info-circle"
                        size={14}
                        color={theme.colors.primary}
                        style={{ marginTop: 2, marginRight: 8 }}
                        solid
                    />
                    <TextM
                        fontSize={12}
                        color={theme.colors.dark}
                        wrap
                        style={{ flex: 1, lineHeight: 17 }}
                    >
                        Nous essayons en priorité de planifier ce cours avec le
                        tuteur habituel de votre enfant. En cas
                        d&apos;indisponibilité, un autre tuteur compatible
                        pourra le prendre en charge.
                    </TextM>
                </View>

                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        backgroundColor: theme.colors.warningLight ?? "#FFF6E5",
                        borderWidth: 1,
                        borderColor: theme.colors.warning ?? "#E8A33D",
                        borderRadius: 10,
                        padding: 12,
                        marginBottom: 12,
                    }}
                >
                    <FontAwesome5
                        name="file-invoice-dollar"
                        size={16}
                        color={theme.colors.warning ?? "#B8782B"}
                        style={{ marginTop: 1, marginRight: 10 }}
                        solid
                    />
                    <View style={{ flex: 1 }}>
                        <TextM
                            fontWeight="Bold"
                            fontSize={13}
                            color={theme.colors.warning ?? "#B8782B"}
                            style={{ marginBottom: 2 }}
                        >
                            Facturation
                        </TextM>
                        <TextM
                            fontSize={12}
                            color={theme.colors.dark}
                            wrap
                            style={{ lineHeight: 17 }}
                        >
                            Le montant de {indicativeTotal} € sera prélevé une
                            fois votre demande traitée par notre équipe.
                        </TextM>
                    </View>
                </View>
            </ScrollView>

            <View style={stickyFooterStyle}>
                {localErrors.length > 0 && (
                    <ErrorTextM
                        errorData={localErrors.join("\n")}
                        style={{ textAlign: "center", marginBottom: 8 }}
                        marginTop={0}
                    />
                )}
                <FilledButtonM
                    label="Confirmer la demande"
                    onPress={() => goToNextStep()}
                    disabled={localErrors.length > 0}
                    style={{ width: "100%" }}
                />
            </View>
        </View>
    )
}
